import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Subject, Observable, timer, NEVER } from 'rxjs';
import { WmrpAnalysisParamSet, WmrpAnalysisParam, WmrpAnalysisRun, WmrpReplacementSchedParam, WmrpResults, WmrpSaveTitleObject } from '../state-management';
import { retry, share, switchMap, takeUntil, catchError } from 'rxjs/operators';

@Injectable()
export class WMRPService implements OnDestroy {
    private ngUnsubscribe: Subject<void> = new Subject();

    wmrpUrl = 'api/wmrp/';
    readonly wmrp_results_url = 'assets/wmrp_results.json';

    httpOptions = {
        headers: new HttpHeaders({
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache'
        })

    };

    private stopPolling = new Subject<void>();
    private stopSchedulePolling = new Subject<void>();

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    stopPoll() {
        this.stopPolling.next();
        this.stopPolling.complete();
    }

    stopSchedulePoll() {
        this.stopSchedulePolling.next();
        this.stopSchedulePolling.complete();
    }

    getMaterials(): any {
        return this.httpClient.get(this.wmrpUrl + 'materials', this.httpOptions);
    }

    getWMRPModule(): any {
        return this.httpClient.get(this.wmrpUrl, this.httpOptions);
    }

    getWMRPTemplates(): Observable<WmrpAnalysisParamSet[]> {
        return this.httpClient.get<WmrpAnalysisParamSet[]>(this.wmrpUrl + 'templates', this.httpOptions);
    }

    getWMRPParameters(id: number = 0): Observable<WmrpAnalysisParam[]> {
        return this.httpClient.get<WmrpAnalysisParam[]>(this.wmrpUrl + 'parameters/' + id, this.httpOptions);
    }

    postWMRPParameters(params: WmrpAnalysisParam[]): Observable<any> {
        return this.httpClient.post(this.wmrpUrl, params, this.httpOptions);
    }

    postAnalysisRunDetails(params: WmrpAnalysisRun): Observable<any> {
        return this.httpClient.post(this.wmrpUrl + 'analysisrundetails', params, this.httpOptions);
    }

    getAnalysisRunDetails(): Observable<any> {
        this.stopPolling = new Subject();
        // return this.httpClient.get<WmrpAnalysisRun[]>(this.wmrpUrl + 'analysisrundetails', this.httpOptions)
        return timer(0, 1000000).pipe(
            switchMap(() => this.httpClient.get(this.wmrpUrl + 'analysisrundetails', this.httpOptions)),
            // .pipe(
            //     catchError(e => NEVER) // TODO: This prevents the poll from stopping on error, but we should tell the user that there's a problem
            // )),
            share(),
            takeUntil(this.stopPolling)
        );
    }

    postRunAnalysisCustom(params: WmrpAnalysisParam[]): Observable<any> {
        const stringifiedParams = params.map(
            (item: WmrpAnalysisParam): WmrpAnalysisParam => {
                return {
                    wmrpModule_Id: item.wmrpModule_Id,
                    enabled: item.enabled,
                    conditions: JSON.stringify(item.conditions)
                }
            }
        );
        return this.httpClient.post(this.wmrpUrl + 'runAnalysisFromCustom', stringifiedParams, this.httpOptions);
    }

    postRunAnalysisTemplate(parameterSetId: number): Observable<any> {
        return this.httpClient.post(this.wmrpUrl + 'runAnalysisFromTemplate/' + parameterSetId, this.httpOptions);
    }

    postGenerateReplacementSchedule(params: WmrpReplacementSchedParam): Observable<any> {
        return this.httpClient.post(this.wmrpUrl + 'replacementSchedule', params, this.httpOptions);
    }

    getReplacementSchedules(fmeReference: string): Observable<any> {
        this.stopSchedulePolling = new Subject();
        // return this.httpClient.get(this.wmrpUrl + 'replacementSchedule/' + fmeReference, this.httpOptions);
        return timer(0, 60000).pipe(
            switchMap(() => this.httpClient.get(this.wmrpUrl + 'replacementSchedule/' + fmeReference, this.httpOptions)),
            // retry(),
            share(),
            takeUntil(this.stopSchedulePolling)
        );
    }

    getWmrtResults(fmeReference: string = '') {
        return this.httpClient.get<WmrpResults[]>(this.wmrpUrl + 'wmrpresults/' + fmeReference, this.httpOptions);
    }

    saveAnalysisTitle(titleObject: WmrpSaveTitleObject) {
        return this.httpClient.post(this.wmrpUrl + 'wmrpAnalysisTitle', titleObject, this.httpOptions);
    }

    DeleteAnalysisRun(jobId: string) {
        return this.httpClient.post(this.wmrpUrl + 'deleteAnalysis/' + jobId, this.httpOptions);
    }

    saveScheduleTitle(titleObject: WmrpSaveTitleObject) {
        return this.httpClient.post(this.wmrpUrl + 'wmrpScheduleTitle', titleObject, this.httpOptions);
    }

    DeleteSchedule(jobId: string) {
        return this.httpClient.post(this.wmrpUrl + 'deleteSchedule/' + jobId, this.httpOptions);
    }

    constructor(
        private httpClient: HttpClient
    ) { }

}
